import { Injectable, inject, signal } from '@angular/core';

import { RoutingService } from './routing.service';
import {
  fallbackMethodRouteOperationType,
  methodRouteOpCssClass,
  normalizeMethodRouteOperationType,
  type MethodRouteOperationType,
} from '../utils/method-route-operations';

/**
 * Loads AdapterMethodRoute operation types from the backend once for admin UIs.
 */
@Injectable({
  providedIn: 'root',
})
export class MethodRouteOperationsService {
  private readonly routing = inject(RoutingService);

  readonly types = signal<MethodRouteOperationType[]>([]);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);

  constructor() {
    this.refresh();
  }

  refresh(): void {
    this.loading.set(true);
    this.error.set(null);
    this.routing.listOperationTypes().subscribe({
      next: (items) => {
        const catalog = items
          .filter((item) => Boolean(item?.value))
          .map((item) => normalizeMethodRouteOperationType(item));
        this.types.set(catalog);
        this.loading.set(false);
        if (catalog.length === 0) {
          this.error.set('No operation types were returned.');
        }
      },
      error: () => {
        this.loading.set(false);
        this.error.set('Could not load operation types.');
      },
    });
  }

  get(value: string): MethodRouteOperationType {
    const found = this.types().find((item) => item.value === value);
    return found ?? fallbackMethodRouteOperationType(value);
  }

  chipClass(value: string): string {
    return methodRouteOpCssClass(this.get(value).css);
  }

  label(value: string): string {
    return this.get(value).title;
  }

  icon(value: string): string {
    return this.get(value).icon;
  }
}
